import { MarkedOptions, Renderer, Marked, Parser } from 'marked';
import { mangle } from 'marked-mangle';
import { markedHighlight } from 'marked-highlight';
import hljs from 'highlight.js';

interface MarkedConverterOptions {
  allowImages?: boolean;
  openLinksInNewWindow?: boolean;
  encodeEmails?: boolean;
  allowCode?: boolean;
  allowHeaders?: boolean;
  allowStrikethrough?: boolean;
  allowHorizontalRule?: boolean;
}

// Cache converters by config
const converterMap = new Map<string, { makeHtml: (text: string) => string }>();

export const createMarkdownConverter = (options: MarkedConverterOptions) => {
  const configString = `allowCode:${options.allowCode}|openLinks:${options.openLinksInNewWindow}|encodeEmails:${options.encodeEmails}|allowImages:${options.allowImages}|allowHeaders:${options.allowHeaders}`;

  if (!converterMap.has(configString)) {
    const converter = createNewConverter(options);
    converterMap.set(configString, converter);
  }

  return converterMap.get(configString);
};

const createNewConverter = ({
  allowImages = false,
  openLinksInNewWindow = false,
  encodeEmails = false,
  allowCode = false,
  allowHeaders = false,
  allowStrikethrough = false,
  allowHorizontalRule = false,
}: MarkedConverterOptions) => {
  // Create a new instance of marked for this converter
  const markedInstance = new Marked();

  // Create options for this specific converter
  const markedOptions: MarkedOptions = {
    breaks: true,
    gfm: true,
  };

  // Apply extensions specific to this instance
  if (encodeEmails) {
    markedInstance.use(mangle());
  }

  if (allowCode) {
    markedInstance.use(
      markedHighlight({
        emptyLangClass: 'hljs',
        langPrefix: 'hljs language-',
        highlight: (code, lang) => {
          const language = hljs.getLanguage(lang) ? lang : 'plaintext';
          return hljs.highlight(code, { language }).value;
        },
      })
    );
  }

  const renderer = new Renderer();
  renderer.parser = new Parser();

  // Handle always-disabled features
  if (!allowHorizontalRule) {
    renderer.hr = () => ''; // horizontalRule
  }
  if (!allowStrikethrough) {
    renderer.del = ({ text }) => text; // strikethrough
  }

  if (!allowImages) {
    renderer.image = () => '';
  }

  if (!allowCode) {
    renderer.code = () => '';
    renderer.codespan = ({ text }) => text;
  }

  if (!allowHeaders) {
    renderer.heading = ({ text }) => text;
  }

  if (openLinksInNewWindow) {
    const linkRenderer = renderer.link;
    renderer.link = (link) => {
      const html = linkRenderer.call(renderer, link);
      return html.replace(
        '<a',
        '<a target="_blank" rel="noopener noreferrer" '
      );
    };
  }

  markedOptions.renderer = renderer;
  markedInstance.setOptions(markedOptions);

  return {
    makeHtml: (text: string) =>
      markedInstance.parse(text, { async: false }) as string,
  };
};
